/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Alert, Snackbar } from "@mui/material";
import { getMessengers, TSlackChannel } from "apis/messangers";
import UI from "@mile-meeting/mile-ui";

import {
  THost,
  TPurpose,
  TRegisterMessengerChannel,
  TRegisterMessengerHost,
  TMessengerChannel,
} from "apis/visitors/setting/type";
import {
  deleteWalkInVisitHost,
  getVisitPurposeHost,
  getWalkInVisitPurpose,
  registerWalkInVisitPurposeChannel,
  registerWalkInVisitPurposeHost,
} from "apis/visitors/setting/walk-in-visit";
import { useEffect, useState } from "react";
import { theme } from "styles/theme";
import { TMember } from "types/user.types";
import Button from "uis/Button";
import ChannelItem from "uis/layout/channel-item";
import HostProfile from "uis/layout/manager-item";
import { AddChannelModal } from "uis/modal/add-channel-modal";
import { AddManagerModal } from "uis/modal/add-manager-modal";

const TabBtn = styled.div<{ isPressed?: boolean }>`
  padding: 12px 24px;
  border-radius: 100px;
  background: ${(props) => (props.isPressed ? theme.primary500 : "white")};
  color: ${(props) => (props.isPressed ? "white" : theme.primary500)};

  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
`;

const IconContainer = styled.div<{ image?: string }>`
  background-image: url(${(props) => props.image});
  width: 48px;
  height: 48px;
  padding: 10px;
  border-radius: 10px;
  background-color: ${theme.neutral50};
  background-repeat: no-repeat;
  background-size: 32px;
  background-position: center;
`;

const EditContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 20px;
  align-items: center;
`;

const SectionTitle = styled.p`
  color: ${theme.neutral900};
  font-size: 16px;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Annotation = styled.p`
  color: ${theme.neutral500};
  font-size: 16px;
  font-weight: 500;
`;

const TableHeader = styled.div`
  border-radius: 12px;
  background: ${theme.neutral50};
  padding: 12px 24px;
  align-items: center;
  display: flex;
  gap: 20px;
`;

const TableHeaderLabel = styled.p`
  font-size: 14px;
  font-weight: 500;
  width: 160px;
`;

const SettingManager = ({ deviceId }: { deviceId: string }) => {
  const [currentTab, setCurrentTab] = useState<TPurpose>();
  const [purposes, setPurposes] = useState<TPurpose[]>([]);
  const [success, setSuccess] = useState<boolean>(false);
  const [isOpenChannelModal, setIsOpenChannelModal] = useState<boolean>(false);
  const [registeredMessenger, setRegisteredMessenger] = useState<TRegisterMessengerChannel[]>([]);
  const [registeredManager, setRegisteredManager] = useState<TRegisterMessengerHost[]>([]);
  const [isOpenManagerModal, setIsOpenManagerModal] = useState<boolean>(false);
  const [usedSlack, setUsedSlack] = useState<boolean>(false);

  useEffect(() => {
    getWalkInVisitPurpose(deviceId).then((res) => {
      if (res) {
        setPurposes(res);
        setCurrentTab(res[0]);
      }
    });

    getMessengers().then((res) => {
      //* 슬랙이 연결되어있는지 확인하는 로직입니다.
      if (res.data?.some((item) => item.messengerProvider === "SLACK")) {
        setUsedSlack(true);
      }
    });
  }, []);

  useEffect(() => {
    onUpdateWalkInVisitInfo();
  }, [currentTab]);

  const onUpdateWalkInVisitInfo = async () => {
    if (currentTab?.id) {
      const res = await getVisitPurposeHost(currentTab.id);

      if (res) {
        setRegisteredManager(res.receivers);
        setRegisteredMessenger(res.channels);
      }
    }
  };

  const onHandleManagerDelete = async (id: string) => {
    const res = await deleteWalkInVisitHost(id);
    if (res) {
      onUpdateWalkInVisitInfo();
      setSuccess(true);
    }
  };

  const onAddChannel = async (channel: TSlackChannel) => {
    if (currentTab?.id) {
      const res = await registerWalkInVisitPurposeChannel(currentTab.id, channel);
      if (res) {
        onUpdateWalkInVisitInfo();
        setSuccess(true);
      }
    }
  };

  const onAddHost = async (members: TMember[]) => {
    if (currentTab?.id) {
      const res = await registerWalkInVisitPurposeHost(currentTab.id, members);
      if (res.success) {
        onUpdateWalkInVisitInfo();
        setSuccess(true);
      }
    }
  };

  return (
    <div>
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success">담당자 설정이 업데이트 되었습니다.</Alert>
      </Snackbar>
      <div
        css={css`
          display: flex;
          padding: 25px 27px;
          background: ${theme.neutral50};
          gap: 12px;
        `}
      >
        {purposes.map((item, index) => {
          return (
            <TabBtn key={index} onClick={() => setCurrentTab(item)} isPressed={item.id === currentTab?.id}>
              {item.content}
            </TabBtn>
          );
        })}
      </div>
      <div
        css={css`
          padding: 40px 30px;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 18px;
            color: ${theme.neutral900};
            font-size: 30px;
            font-weight: 700;
            align-items: center;
          `}
        >
          <IconContainer image={currentTab?.image} />
          <p>{currentTab?.content}</p>
        </div>
      </div>
      <div
        css={css`
          padding: 40px 30px;
        `}
      >
        <EditContainer>
          <SectionTitle>
            {currentTab?.content} 알림 채널
            <br />
            <Annotation>
              {currentTab?.content} 목적으로 당일 방문자가 호출하면 연동된 메신저 채널로 알림이 전송됩니다.
            </Annotation>
          </SectionTitle>

          <UI.Button
            onClick={() => {
              if (usedSlack) setIsOpenChannelModal(true);
            }}
            disabled={!usedSlack}
            size="large"
            title="채널 추가하기"
          />
        </EditContainer>
        <TableHeader>
          <TableHeaderLabel>채널 이름</TableHeaderLabel>
        </TableHeader>
        {registeredMessenger.length > 0 ? (
          registeredMessenger.map((item, index) => {
            return (
              <ChannelItem
                key={index}
                channel={item}
                isMessengerConnected={item.isActive}
                onDelete={() => {
                  if (item.id) onHandleManagerDelete(item.id);
                }}
              />
            );
          })
        ) : (
          <p
            css={css`
              color: ${theme.neutral500};
              font-weight: 400;
              font-size: 16px;
              padding: 24px;
            `}
          >
            등록된 채널이 없습니다
          </p>
        )}
      </div>
      <div
        css={css`
          padding: 40px 30px;
        `}
      >
        <EditContainer>
          <SectionTitle>
            {currentTab?.content} 알림 담당자
            <br />
            <Annotation>
              {currentTab?.content} 목적으로 당일 방문자가 호출하면 해당 담당자의 메신저 DM으로 알람이 전송됩니다.
            </Annotation>
          </SectionTitle>

          <UI.Button onClick={() => setIsOpenManagerModal(true)} title="담당자 추가하기" size="large" />
        </EditContainer>
        <TableHeader>
          <TableHeaderLabel>이름</TableHeaderLabel>
          <TableHeaderLabel>이메일</TableHeaderLabel>
        </TableHeader>
        {registeredManager.length > 0 ? (
          registeredManager.map((item, index) => {
            return (
              <HostProfile
                image={item.host.image ?? ""}
                name={item.host.nickname}
                email={item.host.email}
                key={index}
                onDelete={() => onHandleManagerDelete(item.id)}
              />
            );
          })
        ) : (
          <p
            css={css`
              color: ${theme.neutral500};
              font-weight: 400;
              font-size: 16px;
              padding: 24px;
            `}
          >
            등록된 담당자가 없습니다
          </p>
        )}
      </div>
      <AddChannelModal
        onUpdate={async (value: TSlackChannel) => {
          onAddChannel(value);
        }}
        registered={registeredMessenger}
        modalIsOpen={isOpenChannelModal}
        closeModal={() => setIsOpenChannelModal(false)}
      />

      <AddManagerModal
        onUpdate={async (value: TMember[]) => {
          onAddHost(value);
        }}
        registeredManagers={registeredManager.map((item) => item.host)}
        modalIsOpen={isOpenManagerModal}
        closeModal={() => setIsOpenManagerModal(false)}
      />
    </div>
  );
};

export default SettingManager;
